import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Wrench, BadgeCheck, ArrowRight } from "lucide-react";
import { marketplaceApi, type ServiceItem } from "@/lib/api";
import { topCategoryGroups } from "./FeaturedProducts";

/** "LOGISTICS & FREIGHT" -> "Logistics & freight" (sentence case). */
function sentenceCase(s: string): string {
  const t = s.trim().toLowerCase();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/** Fisher–Yates shuffle (returns a new array). */
function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const ServiceCard = ({ service, index }: { service: ServiceItem; index: number }) => (
  <motion.div
    initial={{ opacity: 0, y: 32, scale: 0.94 }}
    whileInView={{ opacity: 1, y: 0, scale: 1 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.55, delay: Math.min(index * 0.05, 0.5), ease: [0.22, 1, 0.36, 1] }}
    whileHover={{ y: -6, transition: { duration: 0.2 } }}
    className="group relative block bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-xl hover:border-gold/40 transition-[box-shadow,border-color]"
  >
  <Link to={`/marketplace/service/${service.slug}`} className="block">
    <div className="aspect-[16/10] bg-gray-100 relative overflow-hidden">
      {service.main_image_url ? (
        <img
          src={service.main_image_url}
          alt={service.name}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
        />
      ) : (
        <div className="flex items-center justify-center h-full text-gray-300">
          <Wrench size={36} />
        </div>
      )}
      {service.location_type === "online" ? (
        <span className="absolute top-2 left-2 bg-black/80 backdrop-blur text-white text-[10px] font-semibold px-2 py-1 rounded">
          Online
        </span>
      ) : null}
    </div>

    <div className="p-3.5">
      {service.category_name && (
        <p className="text-[10px] text-black tracking-wide mb-1 truncate">{sentenceCase(service.category_name)}</p>
      )}
      <h3 className="text-[13px] font-semibold text-black line-clamp-2 group-hover:text-gold transition-colors leading-snug min-h-[2.6em]">
        {service.name}
      </h3>

      <div className="mt-2">
        {Number(service.price) > 0 ? (
          <span className="text-sm font-bold text-black">
            {service.currency || "$"}{service.discount_price || service.price}
            {service.price_type === "hourly" && <span className="text-[11px] font-normal text-gray-400"> /hr</span>}
            {service.price_type === "per_session" && <span className="text-[11px] font-normal text-gray-400"> /session</span>}
          </span>
        ) : (
          <span className="text-[11px] font-semibold text-gold">Request quote</span>
        )}
      </div>

      {service.owner_is_verified && (
        <div className="flex items-center gap-1 mt-2.5 pt-2.5 border-t border-gray-100 text-[10px] font-medium text-gold">
          <BadgeCheck size={12} /> Verified member
        </div>
      )}
    </div>
  </Link>
  {/* Animated bottom accent line on hover */}
  <span className="pointer-events-none absolute inset-x-0 bottom-0 h-0.5 bg-gold origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300" />
  </motion.div>
);

const FeaturedServices = () => {
  const [services, setServices] = useState<ServiceItem[]>([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const all = await marketplaceApi.listServices();
        if (alive) setServices(shuffle(all));
      } catch {
        /* ignore — section just stays empty */
      }
    })();
    return () => { alive = false; };
  }, []);

  // Four best-stocked categories, one 5-wide row each.
  const groups = useMemo(() => topCategoryGroups(services, 4, 5), [services]);

  if (groups.length === 0) return null;

  return (
    <section className="py-12 bg-background">
      <div className="container mx-auto px-4">
        <div className="flex items-end justify-between mb-6 gap-4">
          <h2 className="text-2xl md:text-3xl font-bold text-black leading-snug">Services</h2>
          <Link
            to="/marketplace/services"
            className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium text-gold hover:underline shrink-0"
          >
            View all <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        <div className="space-y-9">
          {groups.map(group => (
            <div key={group.key}>
              <div className="flex items-end justify-between gap-4 mb-4 pb-2 border-b border-gray-200">
                <h3 className="text-lg md:text-xl font-bold text-black truncate">
                  {sentenceCase(group.name)}
                </h3>
                <Link
                  to={`/marketplace/services?category=${group.key}`}
                  className="inline-flex items-center gap-1 text-sm font-medium text-gold hover:underline shrink-0"
                >
                  See all <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3.5">
                {group.items.map((service, i) => (
                  <ServiceCard key={service.id} service={service} index={i} />
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 text-center sm:hidden">
          <Link
            to="/marketplace/services"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-gold hover:underline"
          >
            View all services <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FeaturedServices;
